<template>
  <div class="panel-card full-height" :class="{ loading: loading }">
    <div class="loading-overlay" v-if="loading"></div>

    <div class="page-header">
      <div class="page-header-main">
        <h3 class="page-header-title">客户对话详情</h3>
        <span class="page-header-sub">
          客户：{{ customerName || `#${customerId}` }}，共 {{ pagination.total }} 条
        </span>
      </div>
      <div class="page-header-actions">
        <span class="chat-refresh-text">自动刷新: {{ refreshInterval / 1000 }}s</span>
        <el-button
          type="primary"
          size="mini"
          class="chat-refresh-btn"
          :loading="refreshing"
          :disabled="loading"
          @click="refreshConversation"
        >
          刷新
        </el-button>
        <button class="btn s xs" @click="goBack" :disabled="loading">返回客户列表</button>
      </div>
    </div>

    <el-tabs v-model="conversationTab" class="conversation-tabs">
      <el-tab-pane label="全部聊天" name="all"></el-tab-pane>
      <el-tab-pane label="销售聊天" name="sales"></el-tab-pane>
      <el-tab-pane label="AI聊天" name="ai"></el-tab-pane>
    </el-tabs>

    <div class="chat-shell">
      <div v-if="loading" class="conv-empty">正在加载对话记录...</div>
      <div v-else-if="!filteredConversation.length" class="conv-empty">当前筛选暂无对话记录</div>
      <div
        v-for="(item, idx) in filteredConversation"
        v-else
        :key="`${idx}-${item.time || ''}-${item.msg_type || ''}`"
        class="chat-item"
        :class="resolveSpeakerClass(item)"
      >
        <div class="chat-bubble">
          <small class="chat-meta">
            {{ resolveSpeakerLabel(item) }}
            <template v-if="item.sender_name"> · {{ item.sender_name }}</template>
            · {{ item.time || "-" }}
          </small>

          <template v-if="isConversationImage(item)">
            <a
              v-if="canPreviewConversationImage(item)"
              class="conversation-image-link"
              :href="getConversationImageUrl(item)"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img
                class="conversation-image"
                :src="getConversationImageUrl(item)"
                alt="聊天图片"
                @error="handleConversationImageError(item)"
              />
            </a>
            <span v-else class="conversation-muted">{{ getConversationImageUnavailableText(item) }}</span>
          </template>

          <template v-else-if="isConversationVoice(item)">
            <audio
              v-if="getConversationVoiceUrl(item)"
              class="conversation-audio"
              controls
              preload="none"
              :src="getConversationVoiceUrl(item)"
            ></audio>
            <span v-else class="conversation-muted">
              语音消息（暂无可播放链接）
              <template v-if="getConversationVoiceDuration(item)">
                · 时长 {{ getConversationVoiceDuration(item) }}s
              </template>
            </span>
          </template>

          <template v-else-if="isConversationFile(item)">
            <a
              v-if="getConversationFileUrl(item)"
              class="conversation-file-link"
              :href="getConversationFileUrl(item)"
              target="_blank"
              rel="noopener noreferrer"
            >
              {{ getConversationFileName(item) || "点击查看文件" }}
            </a>
            <span v-else class="conversation-muted">
              {{ getConversationFileName(item) || "文件消息（暂无可访问链接）" }}
            </span>
          </template>

          <template v-else-if="isConversationVideo(item)">
            <video
              v-if="getConversationVideoUrl(item)"
              class="conversation-video"
              controls
              preload="metadata"
              :src="getConversationVideoUrl(item)"
            ></video>
            <span v-else class="conversation-muted">视频消息（暂无可播放链接）</span>
          </template>

          <template v-else-if="isConversationAppCard(item)">
            <div class="conversation-app-card">
              <div class="conversation-app-header">
                <img
                  v-if="getConversationAppIconUrl(item)"
                  class="conversation-app-icon"
                  :src="getConversationAppIconUrl(item)"
                  alt="应用图标"
                />
                <span class="conversation-app-name">
                  {{ getConversationAppName(item) || getConversationMessageTypeLabelByCode(item && item.msg_type) }}
                </span>
              </div>
              <div class="conversation-app-title">
                {{ getConversationAppTitle(item) || "应用消息" }}
              </div>
              <div v-if="getConversationAppDesc(item)" class="conversation-app-desc">
                {{ getConversationAppDesc(item) }}
              </div>
              <a
                v-if="getConversationAppUrl(item)"
                class="conversation-file-link"
                :href="getConversationAppUrl(item)"
                target="_blank"
                rel="noopener noreferrer"
              >
                打开链接
              </a>
              <div v-else-if="getConversationAppPath(item)" class="conversation-muted">
                {{ getConversationAppPath(item) }}
              </div>
            </div>
          </template>

          <template v-else-if="isConversationCallRecord(item)">
            <div class="conversation-call-record">
              <span class="conversation-call-record-title">{{ getConversationCallRecordText(item) }}</span>
              <span v-if="getConversationCallDuration(item)" class="conversation-call-record-sub">
                时长：{{ getConversationCallDuration(item) }}
              </span>
            </div>
          </template>

          <template v-else-if="isConversationReadReceipt(item)">
            <div class="conversation-call-record">
              <span class="conversation-call-record-title">{{ getConversationReadReceiptText(item) }}</span>
            </div>
          </template>

          <template v-else-if="isConversationRevoke(item)">
            <div class="conversation-revoke">
              <span class="conversation-revoke-title">
                消息已撤回
                <template v-if="getConversationRevokeOriginalTypeLabel(item)">
                  （原消息：{{ getConversationRevokeOriginalTypeLabel(item) }}）
                </template>
              </span>
              <span v-if="getConversationRevokeInfo(item)" class="conversation-revoke-sub">
                {{ getConversationRevokeInfo(item) }}
              </span>
              <span v-if="getConversationRevokeMsgId(item)" class="conversation-revoke-sub">
                原消息ID：{{ getConversationRevokeMsgId(item) }}
              </span>
            </div>
          </template>

          <template v-else-if="isConversationBase64RawMessage(item)">
            <div class="conversation-call-record">
              <span class="conversation-call-record-title">{{ getConversationBase64RawText(item) }}</span>
              <span v-if="getConversationBase64RawLength(item)" class="conversation-call-record-sub">
                原始数据长度：{{ getConversationBase64RawLength(item) }}
              </span>
            </div>
          </template>

          <template v-else>
            {{ getConversationPlainText(item) }}
          </template>
        </div>
      </div>
    </div>

    <div class="pagination-container" v-if="pagination.total > 0">
      <el-pagination
        :current-page="pagination.current_page"
        :page-size="pagination.per_page"
        :page-sizes="[20, 50, 100, 200]"
        layout="total, sizes, prev, pager, next, jumper"
        :total="pagination.total"
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
      />
    </div>
  </div>
</template>

<script>
import { customerApi } from "../../api";

export default {
  name: "CustomerConversationDetail",
  data() {
    return {
      loading: false,
      refreshing: false,
      refreshInterval: 5000,
      refreshTimerId: null,
      conversationTab: "all",
      conversation: [],
      conversationImageLoadFailedMap: {},
      customerName: "",
      pagination: {
        current_page: 1,
        per_page: 50,
        total: 0,
        last_page: 1
      }
    };
  },
  computed: {
    customerId() {
      const id = Number(this.$route.params.id);
      return Number.isFinite(id) && id > 0 ? id : 0;
    },
    filteredConversation() {
      if (this.conversationTab === "ai") {
        return this.conversation.filter((item) => {
          if (!item) return false;
          return item.speaker === "client" || String(item.msg_type) === "auto_reply";
        });
      }
      if (this.conversationTab === "sales") {
        return this.conversation.filter((item) => {
          if (!item) return false;
          if (item.speaker === "client") return true;
          return item.speaker === "sales" && String(item.msg_type) !== "auto_reply";
        });
      }
      return this.conversation;
    }
  },
  created() {
    this.customerName = (this.$route.query.name || "").trim();
    this.loadConversation(true);
    this.startAutoRefresh();
  },
  beforeDestroy() {
    this.stopAutoRefresh();
  },
  watch: {
    "$route.params.id"() {
      this.stopAutoRefresh();
      this.pagination.current_page = 1;
      this.conversationTab = "all";
      this.conversation = [];
      this.conversationImageLoadFailedMap = {};
      this.customerName = (this.$route.query.name || "").trim();
      this.loadConversation(true);
      this.startAutoRefresh();
    }
  },
  methods: {
    goBack() {
      this.stopAutoRefresh();
      this.$router.push("/customers");
    },
    handleSizeChange(size) {
      this.pagination.per_page = size;
      this.pagination.current_page = 1;
      this.loadConversation(true);
    },
    handleCurrentChange(current) {
      this.pagination.current_page = current;
      this.loadConversation(true);
    },
    async refreshConversation() {
      await this.loadConversation(false);
    },
    startAutoRefresh() {
      this.stopAutoRefresh();
      this.refreshTimerId = setInterval(() => {
        if (!this.customerId) return;
        this.loadConversation(false);
      }, this.refreshInterval);
    },
    stopAutoRefresh() {
      if (this.refreshTimerId) {
        clearInterval(this.refreshTimerId);
        this.refreshTimerId = null;
      }
    },
    async loadConversation(showLoading = true) {
      if (!this.customerId) {
        this.conversation = [];
        this.pagination.total = 0;
        return;
      }

      if (showLoading) {
        this.loading = true;
      } else {
        if (this.refreshing) return;
        this.refreshing = true;
      }
      try {
        const data = await customerApi.getCustomerConversation(this.customerId, {
          page: this.pagination.current_page,
          per_page: this.pagination.per_page
        });

        this.conversation = Array.isArray(data.records) ? data.records : (Array.isArray(data.items) ? data.items : []);
        this.conversationImageLoadFailedMap = {};
        if (data.pagination) {
          this.pagination = {
            ...this.pagination,
            ...data.pagination
          };
        } else {
          this.pagination.total = this.conversation.length;
        }
      } catch (e) {
        if (showLoading) {
          this.conversation = [];
          this.pagination.total = 0;
          this.$message.error("加载对话记录失败");
        }
      } finally {
        if (showLoading) {
          this.loading = false;
        } else {
          this.refreshing = false;
        }
      }
    },
    resolveSpeakerClass(item) {
      if (String(item && item.msg_type) === "auto_reply") return "ai";
      return item && item.speaker === "sales" ? "sales" : "client";
    },
    resolveSpeakerLabel(item) {
      if (String(item && item.msg_type) === "auto_reply") return "AI";
      return item && item.speaker === "sales" ? "销售" : "客户";
    },
    parseConversationJson(content, maxDepth = 2) {
      let current = content;
      for (let i = 0; i <= maxDepth; i += 1) {
        if (current == null) return null;
        if (typeof current === "object") return current;
        if (typeof current !== "string") return null;

        const trimmed = current.trim();
        if (!trimmed) return null;
        if (!["{", "[", "\""].includes(trimmed[0])) return null;

        try {
          current = JSON.parse(trimmed);
        } catch (e) {
          return null;
        }
      }
      return typeof current === "object" ? current : null;
    },
    parseConversationPayload(item) {
      const payload = this.parseConversationJson(item && item.content);
      if (!payload || Array.isArray(payload)) return payload;
      if (payload.msgData && typeof payload.msgData === "object") return payload.msgData;
      return payload;
    },
    parseConversationOriginPayload(item) {
      const payload = this.parseConversationJson(item && item.origin_data);
      if (!payload || Array.isArray(payload)) return null;
      if (payload.msgData && typeof payload.msgData === "object") return payload.msgData;
      return payload;
    },
    normalizeConversationMediaUrl(url) {
      if (!url) return "";
      let value = String(url).trim();
      if (!value) return "";
      if (
        (value.startsWith("\"") && value.endsWith("\""))
        || (value.startsWith("'") && value.endsWith("'"))
      ) {
        value = value.slice(1, -1).trim();
      }
      value = value.replace(/\\\//g, "/").replace(/&amp;/gi, "&");
      if (value.startsWith("//")) {
        value = `https:${value}`;
      }
      return /^https?:\/\//i.test(value) ? value : "";
    },
    getConversationRawUrl(item) {
      const content = item && item.content;
      if (typeof content !== "string") return "";
      const trimmed = content.trim();
      const normalized = this.normalizeConversationMediaUrl(trimmed);
      if (normalized) return normalized;
      const matched = trimmed.match(/https?:\/\/[^\s"'<>]+/i);
      return matched ? this.normalizeConversationMediaUrl(matched[0]) : "";
    },
    getConversationMediaUrlFromPayload(payload) {
      if (!payload || Array.isArray(payload)) return "";
      return this.normalizeConversationMediaUrl(
        payload.cloudUrl
          || payload.mediaCloudUrl
          || payload.media_cloud_url
          || payload.fileBigHttpUrl
          || payload.fileMiddleHttpUrl
          || payload.fileThumbHttpUrl
          || payload.fileHttpUrl
          || payload.previewUrl
          || payload.image_display_url
          || payload.localImageUrl
          || payload.local_image_url
          || payload.fileUrl
          || payload.voiceHttpUrl
          || payload.videoHttpUrl
          || payload.downloadUrl
          || payload.url
          || ""
      );
    },
    getConversationMediaUrl(item) {
      const payload = this.parseConversationPayload(item);
      const originPayload = this.parseConversationOriginPayload(item);
      return this.getConversationMediaUrlFromPayload(payload)
        || this.getConversationMediaUrlFromPayload(originPayload);
    },
    getConversationCardPayload(item) {
      const payload = this.parseConversationPayload(item);
      const originPayload = this.parseConversationOriginPayload(item);
      const candidates = [payload, originPayload];
      for (const candidate of candidates) {
        if (!candidate || Array.isArray(candidate) || typeof candidate !== "object") continue;
        if (
          candidate.appId
          || candidate.appName
          || candidate.title
          || candidate.pagePath
          || candidate.username
          || candidate.iconUrl
          || candidate.url
          || candidate.appMediaUrl
          || candidate.desc
        ) {
          return candidate;
        }
      }
      return null;
    },
    isConversationAppCard(item) {
      const type = String(item && item.msg_type);
      if (type === "13" || type === "78") return true;
      return !!this.getConversationCardPayload(item);
    },
    getConversationAppTitle(item) {
      const payload = this.getConversationCardPayload(item);
      if (!payload) return "";
      return payload.title || payload.name || "";
    },
    getConversationAppDesc(item) {
      const payload = this.getConversationCardPayload(item);
      if (!payload) return "";
      return payload.desc || payload.content || "";
    },
    getConversationAppName(item) {
      const payload = this.getConversationCardPayload(item);
      if (!payload) return "";
      return payload.appName || payload.username || "";
    },
    getConversationAppIconUrl(item) {
      const payload = this.getConversationCardPayload(item);
      if (!payload) return "";
      return this.normalizeConversationMediaUrl(
        payload.iconUrl
          || payload.coverImageUrl
          || payload.coverImageHttpUrl
          || payload.coverImageThumbUrl
          || ""
      );
    },
    getConversationAppUrl(item) {
      const payload = this.getConversationCardPayload(item);
      if (!payload) return "";
      return this.normalizeConversationMediaUrl(
        payload.url
          || payload.appMediaUrl
          || payload.linkUrl
          || ""
      );
    },
    getConversationAppPath(item) {
      const payload = this.getConversationCardPayload(item);
      if (!payload) return "";
      if (payload.pagePath) return `小程序路径：${payload.pagePath}`;
      if (payload.username) return `小程序账号：${payload.username}`;
      return "";
    },
    getConversationBase64RawPayload(item) {
      const payload = this.parseConversationPayload(item);
      const originPayload = this.parseConversationOriginPayload(item);
      const candidates = [payload, originPayload];
      for (const candidate of candidates) {
        if (!candidate || Array.isArray(candidate) || typeof candidate !== "object") continue;
        if (candidate.base64RawData) return candidate;
      }
      return null;
    },
    isConversationBase64RawMessage(item) {
      const payload = this.getConversationBase64RawPayload(item);
      if (!payload) return false;
      return !this.isConversationCallRecord(item) && !this.isConversationRevoke(item);
    },
    getConversationBase64RawText(item) {
      const payload = this.getConversationBase64RawPayload(item);
      if (!payload) return "系统消息";
      if (payload.recordWording) return String(payload.recordWording);
      return "系统加密消息";
    },
    getConversationBase64RawLength(item) {
      const payload = this.getConversationBase64RawPayload(item);
      if (!payload || !payload.base64RawData) return "";
      return String(payload.base64RawData).length;
    },
    isLikelyJsonString(value) {
      if (typeof value !== "string") return false;
      const trimmed = value.trim();
      if (!trimmed) return false;
      return trimmed.startsWith("{") || trimmed.startsWith("[");
    },
    getConversationDetailTextFromPayload(payload) {
      if (!payload || typeof payload !== "object") return "";

      const content = typeof payload.content === "string" ? payload.content.trim() : "";
      if (content && !this.isLikelyJsonString(content)) return content;

      const detailText = Array.isArray(payload.moreDetail)
        ? payload.moreDetail
            .map((detail) => {
              if (!detail || typeof detail !== "object") return "";
              return typeof detail.text === "string" ? detail.text.trim() : "";
            })
            .filter(Boolean)
            .join("")
            .trim()
        : "";
      if (detailText) return detailText;

      if (payload.reply && typeof payload.reply === "object") {
        const replyContent = typeof payload.reply.content === "string" ? payload.reply.content.trim() : "";
        if (replyContent && !this.isLikelyJsonString(replyContent)) return replyContent;

        const replyDetailText = Array.isArray(payload.reply.moreDetail)
          ? payload.reply.moreDetail
              .map((detail) => {
                if (!detail || typeof detail !== "object") return "";
                return typeof detail.text === "string" ? detail.text.trim() : "";
              })
              .filter(Boolean)
              .join("")
              .trim()
          : "";
        if (replyDetailText) return replyDetailText;
      }

      return "";
    },
    getConversationPlainText(item) {
      const payload = this.parseConversationPayload(item);
      if (payload && typeof payload === "object" && !Array.isArray(payload)) {
        if (payload.base64RawData) return this.getConversationBase64RawText(item);
        const payloadText = this.getConversationDetailTextFromPayload(payload);
        if (payloadText) return payloadText;
      }
      const content = item && item.content;
      if (content == null) return "";
      if (typeof content === "string") {
        if (this.isLikelyJsonString(content)) return "";
        return content;
      }
      try {
        return JSON.stringify(content);
      } catch (e) {
        return String(content);
      }
    },
    isConversationImage(item) {
      const type = String(item && item.msg_type);
      return ["7", "14", "29", "101", "104"].includes(type);
    },
    getConversationRowKey(item) {
      return (item && (item.id || item.msg_id || item.time || item.sender_name)) || "";
    },
    hasConversationImageLoadFailed(item) {
      const key = this.getConversationRowKey(item);
      return key ? !!this.conversationImageLoadFailedMap[key] : false;
    },
    handleConversationImageError(item) {
      const key = this.getConversationRowKey(item);
      if (!key) return;
      this.$set(this.conversationImageLoadFailedMap, key, true);
    },
    canPreviewConversationImage(item) {
      return !!this.getConversationImageUrl(item) && !this.hasConversationImageLoadFailed(item);
    },
    getConversationImageUnavailableText(item) {
      if (this.hasConversationImageLoadFailed(item)) {
        return "图片链接已失效或已过期";
      }
      return "图片消息（暂无可预览链接）";
    },
    getConversationImageUrl(item) {
      return this.getConversationRawUrl(item) || this.getConversationMediaUrl(item);
    },
    isConversationVoice(item) {
      return String(item && item.msg_type) === "16";
    },
    getConversationVoiceUrl(item) {
      return this.getConversationRawUrl(item) || this.getConversationMediaUrl(item);
    },
    getConversationVoiceDuration(item) {
      const payload = this.parseConversationPayload(item);
      if (!payload) return "";
      const seconds = Number(payload.voiceTime || payload.duration || 0);
      return Number.isFinite(seconds) && seconds > 0 ? Math.round(seconds) : "";
    },
    isConversationFile(item) {
      const type = String(item && item.msg_type);
      return type === "15" || type === "17";
    },
    getConversationFileUrl(item) {
      return this.getConversationRawUrl(item) || this.getConversationMediaUrl(item);
    },
    getConversationFileName(item) {
      const payload = this.parseConversationPayload(item);
      if (!payload) return "";
      return payload.filename || payload.fileName || payload.name || "";
    },
    isConversationVideo(item) {
      const type = String(item && item.msg_type);
      return type === "20" || type === "102";
    },
    getConversationVideoUrl(item) {
      return this.getConversationRawUrl(item) || this.getConversationMediaUrl(item);
    },
    isConversationCallRecord(item) {
      const type = String(item && item.msg_type);
      const payload = this.parseConversationPayload(item);
      return type === "40" || (
        !!payload
        && (payload.recordWording !== undefined || payload.recordType !== undefined || payload.inviteType !== undefined)
      );
    },
    getConversationCallRecordText(item) {
      const payload = this.parseConversationPayload(item);
      if (!payload) return "通话记录";
      if (payload.recordWording) return String(payload.recordWording);
      const recordType = Number(payload.recordType);
      if (recordType === 2) return "对方已拒绝";
      if (recordType === 3) return "对方未接听";
      if (recordType === 4) return "已取消";
      if (recordType === 5) return "通话已结束";
      return "通话记录";
    },
    getConversationCallDuration(item) {
      const payload = this.parseConversationPayload(item);
      if (!payload) return "";
      const seconds = Number(payload.duration || 0);
      if (!Number.isFinite(seconds) || seconds <= 0) return "";
      const mm = Math.floor(seconds / 60);
      const ss = seconds % 60;
      return `${String(mm).padStart(2, "0")}:${String(ss).padStart(2, "0")}`;
    },
    isConversationReadReceipt(item) {
      const type = String(item && item.msg_type);
      return type === "2001" || type === "2005";
    },
    getConversationReadReceiptText(item) {
      const type = String(item && item.msg_type);
      if (type === "2001") return "消息已读通知";
      if (type === "2005") return "消息未读通知";
      return "消息状态通知";
    },
    getConversationRevokePayload(item) {
      const payload = this.parseConversationPayload(item);
      const originPayload = this.parseConversationOriginPayload(item);
      const candidates = [payload, originPayload];
      for (const candidate of candidates) {
        if (!candidate || typeof candidate !== "object" || Array.isArray(candidate)) continue;
        if (
          candidate.revokeMsgUniqueIdentifier !== undefined
          || candidate.revokeUserId !== undefined
          || candidate.revokeTime !== undefined
        ) {
          return candidate;
        }
      }
      return null;
    },
    isConversationRevoke(item) {
      const type = String(item && item.msg_type);
      if (type === "10000" || type === "10002") return true;
      return !!this.getConversationRevokePayload(item);
    },
    formatConversationUnixTimestamp(value) {
      const numeric = Number(value);
      if (!Number.isFinite(numeric) || numeric <= 0) return "";
      const ms = numeric > 1e12 ? numeric : numeric * 1000;
      const date = new Date(ms);
      if (Number.isNaN(date.getTime())) return "";
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      const hour = String(date.getHours()).padStart(2, "0");
      const minute = String(date.getMinutes()).padStart(2, "0");
      const second = String(date.getSeconds()).padStart(2, "0");
      return `${year}-${month}-${day} ${hour}:${minute}:${second}`;
    },
    getConversationRevokeInfo(item) {
      const payload = this.getConversationRevokePayload(item);
      if (!payload) return "";
      const userId = String(payload.revokeUserId ?? "").trim();
      const time = this.formatConversationUnixTimestamp(payload.revokeTime);
      const parts = [];
      if (userId) parts.push(`撤回人ID：${userId}`);
      if (time) parts.push(`撤回时间：${time}`);
      return parts.join(" · ");
    },
    getConversationRevokeMsgId(item) {
      const payload = this.getConversationRevokePayload(item);
      if (!payload) return "";
      return String(payload.revokeMsgUniqueIdentifier ?? "").trim();
    },
    getConversationRevokeOriginalTypeLabel(item) {
      const payload = this.getConversationRevokePayload(item);
      if (!payload) return "";
      const originalType = payload?.extras?.signedMsg?.originalContentType;
      if (originalType == null) return "";
      return this.getConversationMessageTypeLabelByCode(originalType);
    },
    getConversationMessageTypeLabelByCode(msgType) {
      const type = String(msgType);
      if (type === "auto_reply") return "AI回复";
      if (type === "0") return "文本消息";
      if (type === "2") return "文本消息";
      if (type === "13") return "链接消息";
      if (["7", "14", "29", "101", "104"].includes(type)) return "图片消息";
      if (type === "15" || type === "17") return "文件消息";
      if (type === "16") return "语音消息";
      if (type === "18") return "XML消息";
      if (type === "20" || type === "102") return "视频消息";
      if (type === "78") return "小程序消息";
      if (type === "40") return "通话记录";
      if (type === "10000" || type === "10002") return "撤回消息";
      if (type === "2001") return "已读通知";
      if (type === "2005") return "未读通知";
      if (type === "105") return "Markdown消息";
      return "用户消息";
    }
  }
};
</script>

<style scoped>
.full-height {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 130px);
  overflow: hidden;
}

.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.page-header-main {
  display: flex;
  align-items: baseline;
  gap: 8px;
  min-width: 0;
}

.page-header-sub {
  color: #64748b;
  font-size: 13px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.page-header-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.chat-refresh-text {
  font-size: 12px;
  color: #64748b;
}

.chat-refresh-btn {
  cursor: pointer;
}

.chat-shell {
  flex: 1;
  min-height: 0;
  overflow: auto;
  margin-top: 10px;
  display: grid;
  gap: 8px;
  padding-right: 6px;
}

.conversation-tabs {
  margin-top: 10px;
}

.conv-empty {
  border: 1px dashed #cfe1da;
  border-radius: 10px;
  padding: 16px;
  color: #64748b;
  text-align: center;
  background: #f8fcfa;
}

.chat-item {
  display: flex;
  gap: 8px;
  align-items: flex-start;
}

.chat-item.sales {
  justify-content: flex-end;
}

.chat-item.client {
  justify-content: flex-start;
}

.chat-item.ai {
  justify-content: flex-end;
}

.chat-bubble {
  max-width: min(720px, 86%);
  padding: 9px 10px;
  border-radius: 10px;
  border: 1px solid #deebe6;
  background: #f8fcfa;
  color: #0f172a;
  line-height: 1.5;
  font-size: 13px;
}

.chat-item.sales .chat-bubble {
  border-color: #bfdbfe;
  background: #eff6ff;
}

.chat-item.ai .chat-bubble {
  border-color: #a7f3d0;
  background: #ecfdf5;
}

.conversation-image-link,
.conversation-file-link {
  cursor: pointer;
}

.conversation-image {
  max-width: 220px;
  max-height: 220px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  object-fit: cover;
}

.conversation-audio {
  width: 260px;
  max-width: 100%;
}

.conversation-video {
  width: 280px;
  max-width: 100%;
  max-height: 220px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  background: #000;
}

.conversation-file-link {
  color: #0369a1;
  text-decoration: underline;
}

.conversation-muted {
  color: #6b7280;
}

.conversation-call-record {
  display: inline-flex;
  flex-direction: column;
  gap: 4px;
}

.conversation-call-record-title {
  color: #1f2937;
  font-weight: 600;
}

.conversation-call-record-sub {
  color: #6b7280;
  font-size: 12px;
}

.conversation-app-card {
  display: inline-flex;
  flex-direction: column;
  gap: 6px;
  max-width: 360px;
}

.conversation-app-header {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.conversation-app-icon {
  width: 24px;
  height: 24px;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
  object-fit: cover;
}

.conversation-app-name {
  color: #475569;
  font-size: 12px;
}

.conversation-app-title {
  color: #0f172a;
  font-weight: 600;
  line-height: 1.45;
}

.conversation-app-desc {
  color: #475569;
  font-size: 12px;
  line-height: 1.45;
  word-break: break-word;
}

.conversation-revoke {
  display: inline-flex;
  flex-direction: column;
  gap: 4px;
}

.conversation-revoke-title {
  color: #7c2d12;
  font-weight: 600;
}

.conversation-revoke-sub {
  color: #6b7280;
  font-size: 12px;
}

.chat-meta {
  display: block;
  color: #64748b;
  font-size: 11px;
  margin-bottom: 4px;
}

.pagination-container {
  margin-top: 12px;
  padding: 0 4px;
  display: flex;
  justify-content: flex-end;
  align-items: center;
}

.loading {
  position: relative;
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.8);
  border-radius: inherit;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  backdrop-filter: blur(2px);
}

.loading-overlay::after {
  content: "";
  width: 32px;
  height: 32px;
  border: 3px solid rgba(15, 118, 110, 0.1);
  border-top-color: #0f766e;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 900px) {
  .page-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .page-header-main {
    width: 100%;
  }

  .chat-bubble {
    max-width: 100%;
  }
}
</style>
